import { parseTimeCode, debounce } from './common.js';

class NomadVideo {
	constructor( node ) {
		this.node = node;
		this.player = this.node.querySelector( 'wc-player' );

		if ( ! this.player ) {
			return;
		}

		this.video = this.player.querySelector( 'video' );

		if ( ! this.video ) {
			return;
		}

		this.panels = this.node.querySelectorAll( '.wdg-nomad-video__panel' );
		this.panelsButtons = this.node.querySelectorAll( '.wdg-nomad-video__panel-button' );
		this.transcript = this.node.querySelector( '.wdg-nomad-video__panel--transcript' );
		this.sentences = Array.from( this.node.querySelectorAll( '.wdg-nomad-video__sentence' ) ).map( sentence => {
			return {
				node: sentence,
				start: sentence.dataset.start ? parseTimeCode( sentence.dataset.start ) : null,
				end: sentence.dataset.start ? parseTimeCode( sentence.dataset.end ) : null,
				chunks: Array.from( sentence.querySelectorAll( '.wdg-nomad-video__sentence-chunk' ) ).map( chunk => {
					return {
						node: chunk,
						start: chunk.dataset.start ? parseTimeCode( chunk.dataset.start ) : null,
						end: chunk.dataset.start ? parseTimeCode( chunk.dataset.end ) : null,
					};
				} ),
			};
		} );

		this.activeSentenceClassName = 'wdg-nomad-video__sentence--active';
		this.activeSentenceChunkClassName = 'wdg-nomad-video__sentence-chunk--active';
		this.activePanelClassName = 'wdg-nomad-video__panel--active';
		this.activePanelButtonClassName = 'wdg-nomad-video__panel-button--active';

		this.isScrolling = false;
		this.isAutoScrolling = false;

		const handleClick = e => {
			let target = e.target;

			while ( target && target !== this.node ) {
				const isAnnotation = target.matches( '.wdg-nomad-video__annotation' );
				const isSentence = target.matches( '.wdg-nomad-video__sentence' );
				const isNavToggle = target.matches( '.wdg-nomad-video__panel-button[data-panel]' );

				if ( isAnnotation || isSentence ) {
					e.preventDefault();

					if ( target.dataset.start ) {
						const timeCode = parseTimeCode( target.dataset.start );

						if ( this.video && timeCode > -1 ) {
							this.video.currentTime = timeCode;
						}
					}

					break;
				}

				if ( isNavToggle ) {
					e.preventDefault();
					this.setPanel( target.dataset.panel );
					break;
				}

				target = target.parentNode;
			}
		};

		const onPlay = () => {
			this.node.classList.add( 'wdg-nomad-video--playing' );
			this.node.classList.remove( 'wdg-nomad-video--ready' );
			this.node.classList.remove( 'wdg-nomad-video--paused' );
			this.node.classList.remove( 'wdg-nomad-video--ended' );
		};

		const onPause = () => {
			this.node.classList.remove( 'wdg-nomad-video--playing' );
			this.node.classList.remove( 'wdg-nomad-video--ready' );
			this.node.classList.add( 'wdg-nomad-video--paused' );
			this.node.classList.remove( 'wdg-nomad-video--ended' );
		};

		const onEnded = () => {
			this.node.classList.remove( 'wdg-nomad-video--paused' );
			this.node.classList.remove( 'wdg-nomad-video--ready' );
			this.node.classList.remove( 'wdg-nomad-video--playing' );
			this.node.classList.add( 'wdg-nomad-video--ended' );
		};

		const onScroll = e => {
			this.isScrolling = true;
		};

		const onScrollTimeout = e => {
			this.isScrolling = false;
		};

		const handleTimeUpdate = () => {
			const activeIndex = this.getActiveIndex();

			if ( ! activeIndex || ( activeIndex !== this.activeIndex && this.sentences[ this.activeIndex ] ) ) {
				this.sentences[ this.activeIndex ].node.classList.remove( this.activeSentenceClassName );

				for ( const chunk of this.sentences[ this.activeIndex ].chunks ) {
					chunk.node.classList.remove( this.activeSentenceChunkClassName );
				}
			}

			this.activeIndex = activeIndex;

			if ( this.activeIndex > -1 && this.sentences[ this.activeIndex ] ) {
				const sentence = this.sentences[ this.activeIndex ];
				sentence.node.classList.add( this.activeSentenceClassName );

				const activeChunk = this.getActiveIndexFrom( sentence.chunks );

				// prettier-ignore
				for ( const [ i, chunk ] of sentence.chunks.entries() ) {
					if ( i === activeChunk ) {
						chunk.node.classList.add( this.activeSentenceChunkClassName );
					} else {
						chunk.node.classList.remove( this.activeSentenceChunkClassName );
					}
				}

				if ( ! this.isScrolling && ! this.isAutoScrolling ) {
					const isAbove = sentence.node.offsetTop + sentence.node.offsetHeight < this.transcript.scrollTop;
					const isBelow = sentence.node.offsetTop > this.transcript.scrollTop + this.transcript.offsetHeight;

					if ( isAbove || isBelow ) {
						this.isAutoScrolling = true;
						this.transcript.scrollTo( {
							top: sentence.node.offsetTop,
							behavior: 'smooth',
						} );

						setTimeout( () => ( this.isAutoScrolling = false ), 1000 );
					}
				}
			}
		};

		if ( this.player.dataset.poster && this.video ) {
			this.video.poster = this.player.dataset.poster;
		}

		this.activeIndex = this.getActiveIndex();

		this.node.addEventListener( 'click', handleClick );

		if ( this.video ) {
			this.video.addEventListener( 'timeupdate', handleTimeUpdate );
			this.video.addEventListener( 'play', onPlay );
			this.video.addEventListener( 'pause', onPause );
			this.video.addEventListener( 'ended', onEnded );
		}

		if ( this.transcript ) {
			this.transcript.addEventListener( 'scroll', onScroll );
			this.transcript.addEventListener( 'scroll', debounce( onScrollTimeout, 10000 ) );
		}

		if ( window.location.hash ) {
			const hash = window.location.hash.replace( /^#/, '' );

			for ( const sentence of this.sentences ) {
				if ( sentence.node.dataset.start === hash ) {
					if ( this.video.readyState >= 3 ) {
						this.video.currentTime = sentence.start;
					} else {
						this.video.addEventListener( 'loadeddata', () => ( this.video.currentTime = sentence.start ) );
					}
					break;
				}
			}
		}

		handleTimeUpdate();

		if ( this.panelsButtons.length ) {
			setPanel( this.panelsButtons[ 0 ].dataset.panel );
		}

		this.node.classList.add( 'wdg-nomad-video--ready' );
	}

	setPanel( name ) {
		for ( const panel of this.panels ) {
			if ( name === panel.dataset.panelId ) {
				panel.classList.add( activePanelClassName );
			} else {
				panel.classList.remove( activePanelClassName );
			}
		}

		for ( const button of this.panelsButtons ) {
			if ( name === button.dataset.panel ) {
				button.classList.add( activePanelButtonClassName );
			} else {
				button.classList.remove( activePanelButtonClassName );
			}
		}
	}

	getActiveIndexFrom( sentences ) {
		const length = sentences.length;

		// prettier-ignore
		for ( const [ i, sentence ] of sentences.entries() ) {
			// first sentence
			if ( i === 0 && this.video.currentTime < sentence.end ) {
				return i;
			}

			// not first or last
			if ( i > 0 && i < length - 1 && sentences[ i - 1 ].end <= this.video.currentTime && sentence.end > this.video.currentTime ) {
				return i;
			}

			// last sentence
			if ( i >= length - 1 && this.video.currentTime >= sentence.start ) {
				return i;
			}
		}

		return -1;
	}

	getActiveIndex() {
		return this.getActiveIndexFrom( this.sentences );
	}
}

export function initVideo( node ) {
	return new NomadVideo( node );
}

export function initVideos( selector = '.wdg-nomad-video' ) {
	return Array.from( document.querySelectorAll( selector ) ).map( initVideo );
}

document.addEventListener( 'DOMContentLoaded', () => initVideos() );
